import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import classnames from 'classnames';

import {
  Button,
  Checkbox,
  Datepicker,
  Combobox,
  Tooltip,
  Input,
  InputIcon,
  Popover
} from '@salesforce/design-system-react';

import * as dateRangeHelper from '../../utilities/dateRangeHelper';
import rangeOptionValues from '../../constants/dateRangeOptionValues';
import { injectIntl } from 'react-intl';

import './DateRange.scss';

let defaultProps;

const LOCAL_STORAGE_KEY = 'ecs.analytics.defaultRange';

const getDeferredDefaultProps = intl => ({
  assistiveText: {
    nextMonth: 'Next month',
    openCalendar: 'Open Calendar',
    previousMonth: 'Previous month'
  },
  rangeOptions: [
    {
      id: intl.formatMessage({
        id: 'common.daterange.today'
      }),
      label: intl.formatMessage({
        id: 'common.daterange.today'
      }),
      value: rangeOptionValues.TODAY,
      dates: dateRangeHelper.today().dates,
      comparisonValues: dateRangeHelper.today().comparison
    },
    {
      id: intl.formatMessage({
        id: 'common.daterange.last_7_days'
      }),
      label: intl.formatMessage({
        id: 'common.daterange.last_7_days'
      }),
      value: rangeOptionValues.LAST_7_DAYS,
      dates: dateRangeHelper.lastXDays(7).dates
    },
    {
      id: intl.formatMessage({
        id: 'common.daterange.last_30_days'
      }),
      label: intl.formatMessage({
        id: 'common.daterange.last_30_days'
      }),
      value: rangeOptionValues.LAST_30_DAYS,
      dates: dateRangeHelper.lastXDays(30).dates
    }
  ],

  localizedDatePickerLabels: {
    abbreviatedWeekDays: [
      intl.formatMessage({
        id: 'common.daterange.sun'
      }),
      intl.formatMessage({
        id: 'common.daterange.mon'
      }),
      intl.formatMessage({
        id: 'common.daterange.tue'
      }),
      intl.formatMessage({
        id: 'common.daterange.wed'
      }),
      intl.formatMessage({
        id: 'common.daterange.thu'
      }),
      intl.formatMessage({
        id: 'common.daterange.fri'
      }),
      intl.formatMessage({
        id: 'common.daterange.sat'
      })
    ],
    months: [
      intl.formatMessage({
        id: 'common.daterange.january'
      }),
      intl.formatMessage({
        id: 'common.daterange.february'
      }),
      intl.formatMessage({
        id: 'common.daterange.march'
      }),
      intl.formatMessage({
        id: 'common.daterange.april'
      }),
      intl.formatMessage({
        id: 'common.daterange.may'
      }),
      intl.formatMessage({
        id: 'common.daterange.june'
      }),
      intl.formatMessage({
        id: 'common.daterange.july'
      }),
      intl.formatMessage({
        id: 'common.daterange.august'
      }),
      intl.formatMessage({
        id: 'common.daterange.september'
      }),
      intl.formatMessage({
        id: 'common.daterange.october'
      }),
      intl.formatMessage({
        id: 'common.daterange.november'
      }),
      intl.formatMessage({
        id: 'common.daterange.december'
      })
    ],
    today: intl.formatMessage({
      id: 'common.daterange.today'
    })
  }
});

class DateRange extends Component {
  constructor(props) {
    super(props);

    defaultProps = getDeferredDefaultProps(props.intl);

    // Created here so we can use it by reference
    const customRange = {
      startDate: null,
      endDate: null
    };

    let rangeOptions = [];
    if (props.includeCustom) {
      rangeOptions.push({
        id: props.intl.formatMessage({
          id: 'common.daterange.custom'
        }),
        label: props.intl.formatMessage({
          id: 'common.daterange.custom'
        }),
        value: rangeOptionValues.CUSTOM,
        dates: customRange
      });
    }

    if (props.includeAllTime) {
      rangeOptions.push({
        id: props.intl.formatMessage({
          id: 'common.daterange.alltime'
        }),
        label: props.intl.formatMessage({
          id: 'common.daterange.alltime'
        }),
        value: rangeOptionValues.ALL_TIME,
        dates: props.allTimeDates
      });
    }

    rangeOptions = rangeOptions.concat(
      props.rangeOptions ? props.rangeOptions : defaultProps.rangeOptions
    );

    const selectedRangeValue = this.props.selectedRange;
    const selectedRange = rangeOptions.find(
      obj => obj.value === selectedRangeValue
    );

    const selectedRangeIndex = rangeOptions.indexOf(selectedRange);

    const startDate = props.startDate
      ? props.startDate
      : selectedRange.dates.startDate;
    const endDate = props.endDate ? props.endDate : selectedRange.dates.endDate;

    this.state = {
      startDate,
      endDate,
      customRange,
      tmpStartDate: startDate,
      tmpEndDate: endDate,
      endDateIsManuallySet: false,
      dialogOpen: false,
      openPicker: 'none', // other valid options are 'start' and 'end'. Keeps both pickers from being open at the same time.
      rangeOptions,
      selectedRangeIndex: selectedRangeIndex >= 0 ? selectedRangeIndex : 0,
      tmpSelectedRangeIndex: selectedRangeIndex >= 0 ? selectedRangeIndex : 0,
      dateSelectorsVisible: selectedRangeValue !== rangeOptionValues.ALL_TIME,
      inputRendered: false,
      datePickerLabels: defaultProps.localizedDatePickerLabels,
      applyTooltipOpen: false,
      errorText: ''
    };

    customRange.startDate = this.state.tmpStartDate;
    customRange.endDate = this.state.tmpEndDate;
  }

  componentDidMount() {
    this.setState({
      userDefaultRange: parseInt(localStorage.getItem(LOCAL_STORAGE_KEY), 10)
    });
  }

  static momentdate(date) {
    return moment(date)
      .startOf('day')
      .toDate();
  }

  // Function determining which dates are selectable in the start date Datepicker
  checkStartDateDisabled = data => {
    if (this.props.includeFuture) {
      return false;
    }
    const momentdate = DateRange.momentdate(data.date);
    let customValidation = false;
    if (this.props.isStartDateDisabled) {
      customValidation = this.props.isStartDateDisabled(
        data.date,
        this.state.tmpEndDate
      );
    }

    return (
      customValidation ||
      momentdate > DateRange.momentdate() ||
      (this.state.tmpEndDate && momentdate > this.state.tmpEndDate)
    );
  };

  // Function determining which dates are selectable in the end date Datepicker
  checkEndDateDisabled = data => {
    if (this.props.includeFuture) {
      return false;
    }
    const momentdate = DateRange.momentdate(data.date);
    let customValidation = false;
    if (this.props.isEndDateDisabled) {
      customValidation = this.props.isEndDateDisabled(
        data.date,
        this.state.tmpStartDate
      );
    }

    return (
      customValidation ||
      momentdate > DateRange.momentdate() ||
      momentdate < this.state.tmpStartDate
    );
  };

  toggleOpen = willOpen => {
    const newValue = willOpen !== undefined ? willOpen : !this.state.dialogOpen;
    this.setState({
      dialogOpen: newValue
    });
  };

  handlePickerClose = () => {
    this.setState({
      openPicker: 'none'
    });
  };

  handleApply = () => {
    const selectedRange = this.state.rangeOptions[
      this.state.tmpSelectedRangeIndex
    ];
    const selectedRangeOption = selectedRange.value;

    this.setState(
      {
        startDate: this.state.tmpStartDate,
        endDate: this.state.tmpEndDate,
        dialogOpen: false,
        selectedRangeIndex: this.state.tmpSelectedRangeIndex
      },
      () => {
        if (this.state.startDate && this.state.endDate) {
          this.props.onDateRangeChange({
            selectedDate: {
              startDate: this.state.startDate,
              endDate: this.state.endDate
            },
            selectedRangeOption
          });
        }

        this.props.onDateRangeApplied &&
          this.props.onDateRangeApplied(this.state.startDate, this.state.endDate, selectedRangeOption)
      }
    );
  };

  handleCancel = () => {
    this.setState({
      dialogOpen: false,
      tmpEndDate: this.state.endDate,
      tmpStartDate: this.state.startDate,
      tmpSelectedRangeIndex: this.state.selectedRangeIndex,
      errorText: ''
    });
  };

  getInputValue = () => {
    const { formatter, includeAllTime, intl } = this.props;
    const { selectedRangeIndex, rangeOptions } = this.state;

    let value =
      includeAllTime &&
      rangeOptions[selectedRangeIndex].value === rangeOptionValues.ALL_TIME
        ? intl.formatMessage({
            id: 'common.daterange.alltime'
          })
        : `${formatter(this.state.startDate)} - ${formatter(
            this.state.endDate
          )}`;

    if (this.state.compareStartDate) {
      const comparisonString = `${formatter(
        this.state.compareStartDate
      )} - ${formatter(this.state.compareEndDate)}`;
      value = value.concat(' vs. ', comparisonString);
    }

    return value;
  };

  getDropdown = () => {
    const { rangeOptions, tmpSelectedRangeIndex } = this.state;

    return (
      <Combobox
        events={{
          onSelect: (event, data) => {
            this.onRangeSelected(data.selection[0]);
          }
        }}
        options={rangeOptions}
        selection={[rangeOptions[tmpSelectedRangeIndex]]}
        input={
          <Input
            readOnly
            value={rangeOptions[tmpSelectedRangeIndex].label}
            className={classnames('daterange-input')}
          />
        }
        className="daterange-dropdown"
        variant="readonly"
      />
    );
  };

  onRangeSelected = item => {
    //do not allow a property to be deselected
    if (!item) {
      return false;
    }

    const { customRange, rangeOptions } = this.state;

    let newState;
    if (item.value === rangeOptionValues.CUSTOM) {
      newState = {
        tmpSelectedRangeIndex: 0,
        tmpStartDate: customRange.startDate,
        tmpEndDate: customRange.endDate,
        dateSelectorsVisible: true,
        errorText: this.validateDateRange(
          customRange.startDate,
          customRange.endDate
        )
      };
    } else {
      newState = {
        tmpSelectedRangeIndex: rangeOptions.indexOf(item),
        tmpStartDate: item.dates.startDate,
        tmpEndDate: item.dates.endDate,
        dateSelectorsVisible: true,
        errorText: ''
      };
    }

    this.setState(newState, () => {});
  };

  validateDateRange = (startDate, endDate) => {
    let errorText = '';
    const areDatesValid =
      moment(startDate).isValid() && moment(endDate).isValid();
    const { intl } = this.props;
    if (!areDatesValid) {
      errorText = intl.formatMessage({
        id: 'common.daterange.errors.invalid' // 'Please select a valid date range.'
      });
    } else {
      if (!this.props.includeFuture && moment(endDate).isAfter(moment().endOf('day'))) {
        errorText = intl.formatMessage({
          id: 'common.daterange.errors.endDate' // 'End date must be no later than today.'
        });
      }
      if (moment(endDate).isBefore(startDate)) {
        errorText = intl.formatMessage({
          id: 'common.daterange.errors.endDateAfterStartDate' // 'End date must be after start date.'
        });
      }
      if (!this.props.includeFuture && moment(startDate).isAfter(moment())) {
        errorText = intl.formatMessage({
          id: 'common.daterange.errors.startDate' // 'Start date must be today or earlier.'
        });
      }
    }
    return errorText;
  };

  setCustomRange = date => {
    const startDate = date.tmpStartDate
      ? date.tmpStartDate
      : this.state.tmpStartDate;
    let endDate;
    if (!this.state.endDateIsManuallySet) {
      // if the end date was not manually set previously we take on the starting date
      endDate = date.tmpEndDate ? date.tmpEndDate : this.state.tmpStartDate;
    } else {
      endDate = date.tmpEndDate ? date.tmpEndDate : this.state.tmpEndDate;
    }

    const errorText = this.validateDateRange(startDate, endDate);
    // Check custom validation to see if endDate needs cleared
    if (this.props.isEndDateDisabled && date.tmpStartDate) {
      const endDateInvalid = this.props.isEndDateDisabled(
        endDate,
        date.tmpStartDate
      );
      endDate = endDateInvalid ? null : endDate;
      date.tmpEndDate = endDate;

      // This makes me want to vomit but right now it's the only way to clear the value
      // of the datepicker input
      if (endDateInvalid) {
        this.manuallyUpdatePickerValue(this.endDatepickerRef, null);
      }
    }

    this.setState(
      {
        ...date,
        errorText,
        tmpSelectedRangeIndex: 0,
        customRange: {
          startDate,
          endDate
        }
      },
      () => {}
    );
  };

  setInputRef = component => {
    this.inputRef = component;
    // yes, this is a re-render triggered by a render.
    // Dialog/Popper.js cannot place the popover until
    // the trigger/target DOM node is mounted. This
    // way `findDOMNode` is not called and parent
    // DOM nodes are not queried.
    if (!this.state.inputRendered) {
      this.setState({ inputRendered: true });
    }
  };

  setEndDatepickerRef = ref => {
    this.endDatepickerRef = ref;
  };

  // *puking emoji*
  manuallyUpdatePickerValue = (ref, value) => {
    ref.getInstance().setState({
      value,
      inputValue: this.props.formatter(value || ''),
      formattedValue: this.props.formatter(value || '')
    });
  };

  isApplyButtonDisabled = () => {
    const incompleteRange = !this.state.tmpStartDate || !this.state.tmpEndDate;
    let incompleteCompare = false;
    let rangeOverlap = false;
    let invalid = false;
    let tooltipMsg;

    const { intl } = this.props;

    if (
      this.props.enableRangeComparison &&
      this.state.tmpSelectedComparisonIndex !== 0
    ) {
      incompleteCompare =
        !this.state.tmpCompareStart || !this.state.tmpCompareEnd;

      if (incompleteCompare) {
        tooltipMsg = intl.formatMessage({
          id: 'common.SELECT_VALID_RANGE'
        });
      } else {
        rangeOverlap = this.hasRangeOverlap();
      }

      if (rangeOverlap) {
        tooltipMsg = intl.formatMessage({
          id: 'common.NO_RANGE_OVERLAP'
        });
      }
    }

    return {
      disabled:
        incompleteRange ||
        incompleteCompare ||
        rangeOverlap ||
        invalid ||
        !!this.state.errorText,
      tooltip: tooltipMsg
    };
  };

  saveToLocalStorage(tmpSelectedRangeIndex, checked) {
    if (checked) {
      localStorage.setItem(LOCAL_STORAGE_KEY, this.state.tmpSelectedRangeIndex);
    } else {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
    }
  }

  getPopoverBody() {
    // SLDS override
    const { dateSelectorsVisible } = this.state;
    const datesVisibleClass = dateSelectorsVisible ? '' : 'slds-hide';
    const { intl } = this.props;

    return (
      <div>
        <div className="slds-grid slds-grid_vertical-align-center">
          <span className="slds-col slds-grow-none slds-m-right_medium">
            {intl.formatMessage({
              id: 'common.daterange.daterange'
            })}
          </span>
        </div>
        <div>
          <span className="slds-col slds-grid slds-grow slds-text-align_left">
            {this.getDropdown()}
          </span>
          {this.props.dateRangeTooltip && (
            <div className="daterange-tooltip">
              <Tooltip
                align="right"
                content={
                  <div className="text" style={{ width: '140px' }}>
                    {this.props.dateRangeTooltip}
                  </div>
                }
                variant="learnMore"
              />
            </div>
          )}
        </div>
        {this.renderSetDefaultRange()}
        <div className={`slds-grid slds-m-top_x-small ${datesVisibleClass}`}>
          <span className="slds-col daterange-start">
            <Datepicker
              value={this.state.tmpStartDate}
              formattedValue={this.props.formatter(this.state.tmpStartDate)}
              formatter={this.props.formatter}
              parser={this.props.parser}
              isOpen={this.state.openPicker === 'start'}
              input={
                <Input
                  className={this.state.errorText ? 'slds-has-error' : ''}
                />
              }
              align="right"
              relativeYearFrom={0}
              placeholder={intl.formatMessage({
                id: 'common.daterange.start'
              })}
              dateDisabled={this.checkStartDateDisabled}
              onChange={(event, data) => {
                if (!this.state.endDateIsManuallySet) {
                  this.setCustomRange({
                    tmpStartDate: DateRange.momentdate(data.date),
                    tmpEndDate: DateRange.momentdate(data.date)
                  });
                } else {
                  this.setCustomRange({
                    tmpStartDate: DateRange.momentdate(data.date)
                  });
                }

              }}
              onRequestOpen={() => {
                this.setState({
                  openPicker: 'start'
                });
              }}
              onRequestClose={this.handlePickerClose}
              labels={{
                ...this.state.datePickerLabels,
                label: intl.formatMessage({ id: 'common.daterange.startDate' })
              }}
            />
          </span>
          <span className="daterange-connector" />
          <span className="slds-col daterange-end">
            <Datepicker
              ref={this.setEndDatepickerRef}
              value={this.state.tmpEndDate}
              formattedValue={this.props.formatter(this.state.tmpEndDate)}
              formatter={this.props.formatter}
              parser={this.props.parser}
              isOpen={this.state.openPicker === 'end'}
              input={
                <Input
                  className={this.state.errorText ? 'slds-has-error' : ''}
                />
              }
              align="right"
              relativeYearFrom={0}
              placeholder={intl.formatMessage({
                id: 'common.daterange.end'
              })}
              dateDisabled={this.checkEndDateDisabled}
              onChange={(event, data) => {
                this.setState({
                  endDateIsManuallySet: true
                });
                this.setCustomRange({
                  tmpEndDate: DateRange.momentdate(data.date)
                });
              }}
              onRequestOpen={() => {
                this.setState({
                  openPicker: 'end'
                });
              }}
              onRequestClose={this.handlePickerClose}
              labels={{
                ...this.state.datePickerLabels,
                label: intl.formatMessage({ id: 'common.daterange.endDate' })
              }}
            />
          </span>
        </div>
        {this.state.errorText && (
          <div className="slds-grid slds-m-top_x-small slds-text-color_error">
            {this.state.errorText}
          </div>
        )}
      </div>
    );
  }

  getPopoverFooter() {
    const { formatMessage } = this.props.intl;
    const applyDisabled = this.isApplyButtonDisabled();
    let applyButton = (
      <Button
        className="daterange-apply"
        disabled={applyDisabled.disabled}
        onClick={this.handleApply}
        label={formatMessage({
          id: 'common.actions.apply'
        })}
        variant="brand"
      />
    );

    if (applyDisabled.tooltip) {
      applyButton = (
        <div
          className="tooltip-wrapper"
          onMouseEnter={() => {
            this.setState({ applyTooltipOpen: true });
          }}
          onMouseLeave={() => {
            this.setState({ applyTooltipOpen: false });
          }}
        >
          <Tooltip
            key="apply-tooltip"
            isOpen={this.state.applyTooltipOpen}
            content={
              <div className="text" style={{ width: '140px' }}>
                {applyDisabled.tooltip}
              </div>
            }
            align="top"
          >
            <Button
              className="daterange-apply"
              disabled={applyDisabled.disabled}
              onClick={this.handleApply}
              label={formatMessage({
                id: 'common.actions.apply'
              })}
              variant="brand"
            />
          </Tooltip>
        </div>
      );
    }

    return (
      <div>
        <div className="slds-grid slds-grid_align-end">
          <Button
            className="daterange-cancel"
            onClick={this.handleCancel}
            label={formatMessage({ id: 'common.actions.cancel' })}
            variant="neutral"
          />
          {applyButton}
        </div>
      </div>
    );
  }

  renderInputIcon() {
    const assistiveText = {
      ...defaultProps.assistiveText,
      ...this.props.assistiveText
    };

    const inputIcon = (
      <InputIcon
        // Remove || for assistiveText at next breaking change
        assistiveText={{
          icon: assistiveText.openCalendar // eslint-disable-line react/prop-types
        }}
        aria-haspopup
        aria-expanded={this.state.dialogOpen}
        category="utility"
        name="event"
      />
    );

    return inputIcon;
  }

  renderSetDefaultRange() {
    if (this.props.allowDefault && (this.state.tmpSelectedRangeIndex > 0)) {
      return (
        <div className="daterange-default">
          <Checkbox
            variant="base"
            labels={{
              label:this.props.intl.formatMessage({
                id: 'common.daterange.default_range_button'
              })
            }}
            onChange={(evt, { checked }) => {
              this.saveToLocalStorage(this.state.tmpSelectedRangeIndex, checked)
              if (checked) {
                this.setState({ userDefaultRange: this.state.tmpSelectedRangeIndex });
              } else {
                this.setState({ userDefaultRange: null });
              }
            }}
            checked={this.state.userDefaultRange === this.state.tmpSelectedRangeIndex}
          />
        </div>
      );
    } else {
      return null;
    }
  }

  render() {
    const disabledAttribute = this.props.disabled ? 'disabled' : '';
    return (
      <div className={classnames('daterange', this.props.className)}>
        <span className={classnames('daterange-label')}>{this.props.label}</span>
        <Popover
          align="bottom right"
          heading={' '}
          hasNoCloseButton={true}
          isOpen={this.state.dialogOpen}
          className={classnames('daterange-dialog')}
          body={this.getPopoverBody()}
          footer={this.getPopoverFooter()}
        >
          <Button
            variant="base"
            disabled={this.props.disabled}
          >
            <Input
              readOnly
              value={this.getInputValue()}
              iconRight={this.renderInputIcon()}
              onClick={() => {
                this.toggleOpen();
              }}
              inputRef={this.setInputRef}
              className={classnames(`daterange-input ${disabledAttribute}` )}
            />
          </Button>
        </Popover>
      </div>
    );
  }
}

DateRange.propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.bool,
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  onDateRangeChange: PropTypes.func,
  assistiveText: PropTypes.shape({
    nextMonth: PropTypes.string,
    openCalendar: PropTypes.string,
    previousMonth: PropTypes.string
  }),
  label: PropTypes.string,
  parser: PropTypes.func,
  formatter: PropTypes.func,
  includeCustom: PropTypes.bool,
  includeAllTime: PropTypes.bool,
  includeFuture: PropTypes.bool,
  rangeOptions: PropTypes.array,
  selectedRange: PropTypes.string,
  allTimeDates: PropTypes.shape({
    start: PropTypes.object,
    end: PropTypes.object
  }),
  menuPosition: PropTypes.oneOf([
    'absolute',
    'overflowBoundaryElement',
    'relative'
  ]),
  isStartDateDisabled: PropTypes.func,
  isEndDateDisabled: PropTypes.func,
  dateRangeTooltip: PropTypes.string,
  onDateRangeApplied: PropTypes.func,
  intl: PropTypes.object.isRequired
};

DateRange.defaultProps = {
  parser(str) {
    return moment(str, 'l').toDate();
  },
  formatter(date) {
    return moment(date).format('l');
  },
  includeCustom: true,
  includeAllTime: false,
  allTimeDates: dateRangeHelper.allTimeDates().dates,
  selectedRange: rangeOptionValues.TODAY,
  allowDefault: false
};

export default injectIntl(DateRange);
